import React from "react";
import { useEditorState } from "@tiptap/react";
import { Bold, Italic, Underline, List, Quote } from "lucide-react";
import { toast } from "sonner";
import { applyRichFormat } from "@/lib/richFormat";
import { useApp } from "@/context/AppContext";

// Petite barre de mise en forme (discrète, mobile-friendly) posée au-dessus du champ de publication.
// Deux modes : `editor` (Tiptap, lot 778 — aperçu en direct, bouton allumé quand le format est actif sous le curseur)
// ou `textareaRef` + `value` / `onChange` (repères **gras**, *italique*, __souligné__, « - », « > » insérés dans un <textarea>).
const BTNS = [
  { kind: "bold", Icon: Bold, label: "Gras" },
  { kind: "italic", Icon: Italic, label: "Italique" },
  { kind: "underline", Icon: Underline, label: "Souligné" },
  { kind: "list", Icon: List, label: "Liste à puces" },
  { kind: "quote", Icon: Quote, label: "Citation" },
];
const EDITOR_CMD = {
  bold: (c) => c.toggleBold(), italic: (c) => c.toggleItalic(), underline: (c) => c.toggleUnderline(),
  list: (c) => c.toggleBulletList(), quote: (c) => c.toggleBlockquote(),
};
const ACTIVE = { bold: "bold", italic: "italic", underline: "underline", list: "bulletList", quote: "blockquote" };

export const RichTextToolbar = ({ editor = null, textareaRef, value, onChange, tone = "light", className = "", testId = "rich-toolbar" }) => {
  const { t } = useApp();
  const dark = tone === "dark";
  const active = useEditorState({ editor, selector: ({ editor: ed }) => (ed ? Object.fromEntries(Object.entries(ACTIVE).map(([k, n]) => [k, ed.isActive(n)])) : {}) }) || {};
  const apply = (kind) => {
    if (editor) { EDITOR_CMD[kind](editor.chain().focus()).run(); return; }
    const el = textareaRef?.current;
    if (!el) return;
    const { value: nv, selStart, selEnd, hint } = applyRichFormat(el, kind, value ?? el.value ?? "");
    if (hint) { toast.info(t("composer.format_select_hint", "Sélectionne d'abord le texte à mettre en forme (ou pose le curseur sur un mot).")); return; }
    onChange(nv);
    // Restaure le focus + la sélection après le re-render (champ contrôlé).
    setTimeout(() => { try { el.focus(); el.setSelectionRange(selStart, selEnd); } catch { /* noop */ } }, 0);
  };
  return (
    <div
      className={`flex items-center gap-0.5 ${dark ? "" : "rounded-lg"} ${className}`}
      style={dark ? undefined : { background: "var(--yalola-surface-alt)" }}
      role="toolbar"
      aria-label="Mise en forme du texte"
      data-testid={testId}
    >
      {BTNS.map(({ kind, Icon, label }) => {
        const on = !!active[kind];
        return (
          <button
            key={kind}
            type="button"
            title={label}
            aria-label={label}
            aria-pressed={on}
            onMouseDown={(e) => { e.preventDefault(); apply(kind); }}
            className={`h-8 w-8 grid place-items-center rounded-lg transition-colors active:scale-95 ${on ? "text-white" : dark ? "text-white/85 hover:bg-white/10 active:bg-white/15" : "hover:bg-[color:var(--yalola-surface)]"}`}
            style={on ? { background: "var(--yalola-primary)" } : dark ? undefined : { color: "var(--yalola-text-muted)" }}
            data-testid={`${testId}-${kind}`}
            data-active={on ? "1" : "0"}
          >
            <Icon size={16} />
          </button>
        );
      })}
    </div>
  );
};

export default RichTextToolbar;
